import React, { createContext, useContext } from 'react';
import type {
  ScheduleUpsertModalB50ContextValue,
  ScheduleUpsertModalCV50ContextValue,
  ScheduleCreateModalSharedContextValue,
  UseScheduleUpsertModalB50Params,
  UseScheduleUpsertModalCV50Params,
  UseScheduleCreateModalParams,
} from '../types/MachineDetailsRobotSchedule.types';
import { useScheduleUpsertModalB50State } from '../hooks/useScheduleUpsertModalB50';
import { useScheduleUpsertModalCV50State } from '../hooks/useScheduleUpsertModalCV50';

const ScheduleUpsertModalB50Context = createContext<ScheduleUpsertModalB50ContextValue | null>(null);
const ScheduleUpsertModalCV50Context = createContext<ScheduleUpsertModalCV50ContextValue | null>(null);

const toSharedContextValue = (
  value: ScheduleUpsertModalB50ContextValue | ScheduleUpsertModalCV50ContextValue
): ScheduleCreateModalSharedContextValue => ({
  form: value.form,
  robot: value.robot,
  hasScheduleChanges: value.hasScheduleChanges,
  handleFormValuesChange:
    value.handleFormValuesChange as ScheduleCreateModalSharedContextValue['handleFormValuesChange'],
  handleValidateFields: value.handleValidateFields,
  handleSubmit: value.handleSubmit as ScheduleCreateModalSharedContextValue['handleSubmit'],
  handleCancel: value.handleCancel,
  scheduleName: value.scheduleName,
  handleSaveChanges: value.handleSaveChanges,
  isEditingScheduleName: value.isEditingScheduleName,
  isScheduleNameInvalid: value.isScheduleNameInvalid,
  handleToggleEditingScheduleName: value.handleToggleEditingScheduleName,
});

const ScheduleUpsertModalB50Provider = ({
  children,
  ...params
}: React.PropsWithChildren<UseScheduleUpsertModalB50Params>): React.JSX.Element => {
  const value = useScheduleUpsertModalB50State(params);

  return <ScheduleUpsertModalB50Context.Provider value={value}>{children}</ScheduleUpsertModalB50Context.Provider>;
};

export const useScheduleUpsertModalB50 = (): ScheduleUpsertModalB50ContextValue => {
  const context = useContext(ScheduleUpsertModalB50Context);

  if (!context) {
    throw new Error('useScheduleUpsertModalB50 must be used within ScheduleCreateModalProvider (B50)');
  }

  return context;
};

const ScheduleUpsertModalCV50Provider = ({
  children,
  ...params
}: React.PropsWithChildren<UseScheduleUpsertModalCV50Params>): React.JSX.Element => {
  const value = useScheduleUpsertModalCV50State(params);

  return <ScheduleUpsertModalCV50Context.Provider value={value}>{children}</ScheduleUpsertModalCV50Context.Provider>;
};

export const useScheduleUpsertModalCV50 = (): ScheduleUpsertModalCV50ContextValue => {
  const context = useContext(ScheduleUpsertModalCV50Context);

  if (!context) {
    throw new Error('useScheduleUpsertModalCV50 must be used within ScheduleCreateModalProvider (CV50)');
  }

  return context;
};

/**
 * Shared modal-shell accessors. Reads both contexts unconditionally (Rules of Hooks),
 * then returns whichever provider is active.
 */
export const useScheduleCreateModal = (): ScheduleCreateModalSharedContextValue => {
  const b50Context = useContext(ScheduleUpsertModalB50Context);
  const cv50Context = useContext(ScheduleUpsertModalCV50Context);
  const context = b50Context ?? cv50Context;

  if (!context) {
    throw new Error('useScheduleCreateModal must be used within ScheduleCreateModalProvider');
  }

  return toSharedContextValue(context);
};

export const ScheduleCreateModalProvider = ({
  children,
  isB50,
  ...params
}: React.PropsWithChildren<UseScheduleCreateModalParams>): React.JSX.Element => {
  if (isB50) {
    return <ScheduleUpsertModalB50Provider {...params}>{children}</ScheduleUpsertModalB50Provider>;
  }

  return <ScheduleUpsertModalCV50Provider {...params}>{children}</ScheduleUpsertModalCV50Provider>;
};
